import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { Locale, detectLocale, t as translate, normalizeLocale } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface LocaleContextType {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string>) => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());

  // Sync with profile when user logs in
  useEffect(() => {
    if (profile && (profile as any).locale_preference) {
      const pref = (profile as any).locale_preference as Locale;
      if (["en", "lv", "ru"].includes(pref)) {
        setLocaleState(pref);
        localStorage.setItem("locale_preference", pref);
      }
    }
  }, [profile]);

  const setLocale = useCallback(async (l: Locale) => {
    setLocaleState(l);
    localStorage.setItem("locale_preference", l);

    if (user) {
      try {
        await supabase
          .from("profiles")
          .update({
            locale_preference: l,
            locale_detected: normalizeLocale(navigator.language),
            locale_updated_at: new Date().toISOString(),
          } as any)
          .eq("id", user.id);
      } catch {
        // non-fatal
      }
    }
  }, [user]);

  const t = useCallback(
    (key: string, vars?: Record<string, string>) => translate(key, locale, vars),
    [locale]
  );

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}
